import TitrePage from "../components/charte/TitrePage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box, Typography, Button, TextField, MenuItem, Select, FormControl,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Switch, Chip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";

import { loaderOff, loaderOn, openSnackbar } from "../redux/actions";
import {
  Produit, ProduitCategorie, getCategories, getProduits,
  creerCategorie, modifierCategorie, supprimerCategorie, deplacerCategorie as deplacerCategorieProduit,
  creerProduit, modifierProduit, basculerActif, messageErreur,
} from "../utilities/produitService";
import { AIDE_REGLAGES } from "../aide/textesAide";

/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  card: "var(--c-surface)",
  card2: "var(--c-surface-2)",
  border: "var(--c-border)",
  accent: "#1F1E1B",
  accentLight: "#57534A",
  accentMuted: "rgba(31,30,27,0.07)",
  text: "#1F1E1B",
  textSub: "#57534A",
  textMuted: "#57534A",
  success: "#356A0F",
  warning: "#7C5600",
  error: "#A23A0D",
} as const;

const champSx = {
  "& .MuiOutlinedInput-root": {
    background: C.card2, color: C.text, borderRadius: "8px",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.accentLight },
  },
  "& .MuiInputLabel-root": { color: C.textSub, fontSize: 13 },
};

/**
 * Administration du référentiel produits (F3, lot 2).
 *
 * Le référentiel est livré peuplé — 66 produits, 10 catégories — et cette
 * page sert à le faire évoluer à partir des premiers relevés réels, sans
 * redéploiement.
 *
 * Un produit ne se supprime pas, il se désactive : il resterait référencé par
 * des quêtes passées, et la donnée historique doit rester lisible. Une
 * catégorie vide, elle, se supprime.
 */
export default function Produits() {
  const dispatch = useDispatch();

  const [categories, setCategories] = useState<ProduitCategorie[]>([]);
  const [produits, setProduits]     = useState<Produit[]>([]);
  const [filtreCategorie, setFiltreCategorie] = useState<number | "toutes">("toutes");
  const [masquerInactifs, setMasquerInactifs] = useState(false);

  const [dialogCategorie, setDialogCategorie] = useState<{ ouvert: boolean; id?: number; libelle: string }>(
    { ouvert: false, libelle: "" });
  const [dialogProduit, setDialogProduit] = useState<{ ouvert: boolean; id?: number; libelle: string; categorieId: number | "" }>(
    { ouvert: false, libelle: "", categorieId: "" });
  const [confirmation, setConfirmation] = useState<{ ouvert: boolean; categorie?: ProduitCategorie }>(
    { ouvert: false });

  const charger = useCallback(async () => {
    dispatch(loaderOn());
    try {
      const [cats, prods] = await Promise.all([getCategories(), getProduits()]);
      setCategories(cats);
      setProduits(prods);
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Impossible de charger le référentiel."), "red"));
    } finally {
      dispatch(loaderOff());
    }
  }, [dispatch]);

  useEffect(() => { charger(); }, [charger]);

  const produitsAffiches = useMemo(() => produits.filter(p =>
    (filtreCategorie === "toutes" || p.categorie?.id === filtreCategorie) &&
    (!masquerInactifs || p.actif)
  ), [produits, filtreCategorie, masquerInactifs]);

  const compteParCategorie = useMemo(() => {
    const n: Record<number, number> = {};
    produits.forEach(p => { if (p.categorie) n[p.categorie.id] = (n[p.categorie.id] ?? 0) + 1; });
    return n;
  }, [produits]);

  /* ── Catégories ── */

  const enregistrerCategorie = async () => {
    const libelle = dialogCategorie.libelle.trim();
    if (!libelle) return;
    try {
      if (dialogCategorie.id) await modifierCategorie(dialogCategorie.id, { libelle });
      else                    await creerCategorie(libelle);
      setDialogCategorie({ ouvert: false, libelle: "" });
      dispatch(openSnackbar("Catégorie enregistrée.", "green"));
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Enregistrement impossible."), "red"));
    }
  };

  /* L'ordre décide de la place dans les sélecteurs : sans cela une catégorie
     ajoutée resterait indéfiniment en dernier. L'échange se fait en un seul
     appel, atomique côté serveur. */
  const deplacerCategorie = async (index: number, sens: -1 | 1) => {
    const courante = categories[index];
    if (!courante || !categories[index + sens]) return;
    try {
      await deplacerCategorieProduit(courante.id, sens < 0 ? "haut" : "bas");
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Réordonnancement impossible."), "red"));
    }
  };

  const confirmerSuppressionCategorie = async () => {
    const cat = confirmation.categorie;
    if (!cat) return;
    try {
      await supprimerCategorie(cat.id);
      dispatch(openSnackbar(`Catégorie « ${cat.libelle} » supprimée.`, "green"));
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Suppression impossible."), "red"));
    } finally {
      setConfirmation({ ouvert: false });
    }
  };

  /* ── Produits ── */

  const enregistrerProduit = async () => {
    const libelle = dialogProduit.libelle.trim();
    if (!libelle || dialogProduit.categorieId === "") return;
    try {
      if (dialogProduit.id) {
        await modifierProduit(dialogProduit.id, { libelle, categorieId: Number(dialogProduit.categorieId) });
      } else {
        await creerProduit(libelle, Number(dialogProduit.categorieId));
      }
      setDialogProduit({ ouvert: false, libelle: "", categorieId: "" });
      dispatch(openSnackbar("Produit enregistré.", "green"));
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Enregistrement impossible."), "red"));
    }
  };

  const changerActif = async (p: Produit) => {
    if (!window.confirm(`${p.actif ? "Désactiver" : "Réactiver"} le produit « ${p.libelle} » ?`)) return;
    try {
      await basculerActif(p.id, !p.actif);
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Changement impossible."), "red"));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 } }}>
      <TitrePage domaine="reglages" aide={AIDE_REGLAGES.produits}>Produits</TitrePage>
      <Typography sx={{ fontSize: 13, color: C.textSub, mb: 3 }}>
        Produits génériques, jamais des marques — « Savon », pas « Savon Madar ».
        Un produit ne se supprime pas : il se désactive, pour rester lisible sur
        les quêtes qui le référencent.
      </Typography>

      {/* ── Catégories ── */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            Catégories <span style={{ color: C.textMuted, fontWeight: 400 }}>({categories.length})</span>
          </Typography>
          <Button
            size="small" startIcon={<AddRoundedIcon />}
            onClick={() => setDialogCategorie({ ouvert: true, libelle: "" })}
            sx={{ textTransform: "none", color: C.accentLight }}
          >
            Nouvelle catégorie
          </Button>
        </Box>
        <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 1.5 }}>
          L'ordre ci-dessous est celui des listes déroulantes de création de quête.
        </Typography>

        {categories.map((cat, i) => (
          <Box key={cat.id} sx={{
            display: "flex", alignItems: "center", gap: 1,
            py: 0.75, borderBottom: i < categories.length - 1 ? `1px solid ${C.border}` : "none",
          }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <IconButton size="small" disabled={i === 0}
                onClick={() => deplacerCategorie(i, -1)} sx={{ p: 0, color: C.textMuted }}>
                <ArrowUpwardRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
              <IconButton size="small" disabled={i === categories.length - 1}
                onClick={() => deplacerCategorie(i, 1)} sx={{ p: 0, color: C.textMuted }}>
                <ArrowDownwardRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
            <Typography sx={{ flex: 1, fontSize: 13, color: C.text }}>{cat.libelle}</Typography>
            <Chip size="small" label={`${compteParCategorie[cat.id] ?? 0} produit(s)`}
              sx={{ background: C.accentMuted, color: C.accentLight, fontSize: 11, height: 22 }} />
            <IconButton size="small" onClick={() => setDialogCategorie({ ouvert: true, id: cat.id, libelle: cat.libelle })}>
              <EditRoundedIcon sx={{ fontSize: 16, color: C.textSub }} />
            </IconButton>
            <IconButton size="small" onClick={() => setConfirmation({ ouvert: true, categorie: cat })}>
              <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: C.error }} />
            </IconButton>
          </Box>
        ))}
      </Box>

      {/* ── Produits ── */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, flex: 1 }}>
            Produits <span style={{ color: C.textMuted, fontWeight: 400 }}>({produitsAffiches.length})</span>
          </Typography>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select value={filtreCategorie} sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
              onChange={e => setFiltreCategorie(e.target.value === "toutes" ? "toutes" : Number(e.target.value))}>
              <MenuItem value="toutes">Toutes les catégories</MenuItem>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.libelle}</MenuItem>)}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Switch size="small" checked={masquerInactifs} onChange={e => setMasquerInactifs(e.target.checked)} />
            <Typography sx={{ fontSize: 12, color: C.textSub }}>Actifs seulement</Typography>
          </Box>

          <Button size="small" startIcon={<AddRoundedIcon />}
            onClick={() => setDialogProduit({ ouvert: true, libelle: "", categorieId: categories[0]?.id ?? "" })}
            sx={{ textTransform: "none", color: C.accentLight }}>
            Nouveau produit
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Produit", "Catégorie", "Statut", ""].map((t, i) => (
                  <TableCell key={i} sx={{ color: C.textMuted, borderColor: C.border, fontSize: 11,
                    textTransform: "uppercase", letterSpacing: "0.08em" }}>{t}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {produitsAffiches.map(p => (
                <TableRow key={p.id}>
                  <TableCell sx={{ color: p.actif ? C.text : C.textMuted, borderColor: C.border, fontSize: 13 }}>
                    {p.libelle}
                  </TableCell>
                  <TableCell sx={{ color: C.textSub, borderColor: C.border, fontSize: 12 }}>
                    {p.categorie?.libelle ?? "—"}
                  </TableCell>
                  <TableCell sx={{ borderColor: C.border }}>
                    <Chip size="small" label={p.actif ? "Actif" : "Désactivé"}
                      sx={{
                        height: 22, fontSize: 11, fontWeight: 600,
                        background: p.actif ? "rgba(53,106,15,0.12)" : "rgba(87,83,74,0.15)",
                        color: p.actif ? C.success : C.textMuted,
                      }} />
                  </TableCell>
                  <TableCell align="right" sx={{ borderColor: C.border }}>
                    <Button size="small" onClick={() => changerActif(p)}
                      sx={{ textTransform: "none", fontSize: 12, color: p.actif ? C.warning : C.success }}>
                      {p.actif ? "Désactiver" : "Réactiver"}
                    </Button>
                    <IconButton size="small" onClick={() => setDialogProduit({
                      ouvert: true, id: p.id, libelle: p.libelle, categorieId: p.categorie?.id ?? "" })}>
                      <EditRoundedIcon sx={{ fontSize: 16, color: C.textSub }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {produitsAffiches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ color: C.textMuted, borderColor: C.border, fontSize: 13, py: 3, textAlign: "center" }}>
                    Aucun produit pour ce filtre.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Dialogue catégorie ── */}
      <Dialog open={dialogCategorie.ouvert} onClose={() => setDialogCategorie({ ouvert: false, libelle: "" })}
        PaperProps={{ sx: { background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", minWidth: 380 } }}>
        <DialogTitle sx={{ color: C.text, fontSize: 15, fontWeight: 700 }}>
          {dialogCategorie.id ? "Renommer la catégorie" : "Nouvelle catégorie"}
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Libellé" sx={{ ...champSx, mt: 1 }}
            value={dialogCategorie.libelle}
            onChange={e => setDialogCategorie(d => ({ ...d, libelle: e.target.value }))} />
          {dialogCategorie.id && (
            <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 1.5 }}>
              Renommer ne casse rien : les quêtes référencent l'identifiant, pas le libellé.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogCategorie({ ouvert: false, libelle: "" })}
            sx={{ textTransform: "none", color: C.textSub }}>Annuler</Button>
          <Button onClick={enregistrerCategorie} variant="contained"
            sx={{ textTransform: "none", background: C.accent }}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialogue produit ── */}
      <Dialog open={dialogProduit.ouvert} onClose={() => setDialogProduit({ ouvert: false, libelle: "", categorieId: "" })}
        PaperProps={{ sx: { background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", minWidth: 380 } }}>
        <DialogTitle sx={{ color: C.text, fontSize: 15, fontWeight: 700 }}>
          {dialogProduit.id ? "Modifier le produit" : "Nouveau produit"}
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Libellé" sx={{ ...champSx, mt: 1, mb: 2 }}
            value={dialogProduit.libelle}
            onChange={e => setDialogProduit(d => ({ ...d, libelle: e.target.value }))} />
          <FormControl fullWidth size="small">
            <Select value={dialogProduit.categorieId} displayEmpty
              sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
              onChange={e => setDialogProduit(d => ({ ...d, categorieId: Number(e.target.value) }))}>
              <MenuItem value="" disabled>Choisir une catégorie</MenuItem>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.libelle}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 1.5 }}>
            Produit générique et non une marque. Un libellé déjà pris est refusé,
            casse comprise.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogProduit({ ouvert: false, libelle: "", categorieId: "" })}
            sx={{ textTransform: "none", color: C.textSub }}>Annuler</Button>
          <Button onClick={enregistrerProduit} variant="contained"
            sx={{ textTransform: "none", background: C.accent }}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirmation de suppression de catégorie ── */}
      <Dialog open={confirmation.ouvert} onClose={() => setConfirmation({ ouvert: false })}
        PaperProps={{ sx: { background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px" } }}>
        <DialogTitle sx={{ color: C.text, fontSize: 15, fontWeight: 700 }}>Supprimer la catégorie ?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: C.textSub }}>
            « {confirmation.categorie?.libelle} » sera supprimée. L'opération est
            refusée si elle contient encore des produits.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation({ ouvert: false })}
            sx={{ textTransform: "none", color: C.textSub }}>Annuler</Button>
          <Button onClick={confirmerSuppressionCategorie} variant="contained"
            sx={{ textTransform: "none", background: C.error }}>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
